import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TNTable from "@/components/TNTable";
import { CButton, CCol, CFormInput, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useTranslationContext } from "@/context/TranslationContext";
import { useListCms, useDeleteCms, useUpdateCmsStatus } from "@/hooks/useCms";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import { cms } from "@/constants/translation/cms.translation";
import { masters, dashboard } from "@/libs/routes";
import { dateTimeFormat, setFormatDate } from "@/utils/dateFormate";
import constants from "@/libs/constant";

// API responses pass every value through the app's shared encryption round-trip, which
// stringifies booleans (true -> "true"), so native truthiness on the raw value is wrong.
const isActiveValue = (value) => value === true || value === constants.status.apiStatus.true;

/**
 * ListCmsPage Component
 *
 * Lists CMS pages with search, pagination, an active/inactive toggle, edit and delete.
 * "Add CMS Page" creates a new page.
 *
 * @returns {JSX.Element} React component for listing CMS pages.
 */
const ListCmsPage = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(constants.pagination.defaultPage);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const searchTimeout = useRef(null);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, []);

  /** Debounced search: waits 400 ms after typing before querying. */
  const handleSearch = (e) => {
    const value = e.target.value;
    setTempSearchText(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchText(value);
      setCurrentPage(constants.pagination.defaultPage);
    }, 400);
  };

  const handleClearFilter = () => {
    setTempSearchText("");
    setSearchText("");
    setCurrentPage(constants.pagination.defaultPage);
  };

  const { data: listData, isLoading, refetch } = useListCms({
    page: currentPage || constants.pagination.defaultPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText && searchText.trim() ? { search_text: searchText.trim() } : {}),
  });

  const cmsList = listData?.data?.cmsList || [];
  const paginationData = listData?.data?.pagination || null;

  const handleEditClick = useCallback(
    (uuid) => navigate(masters.cms.edit.replace(":id", uuid)),
    [navigate]
  );

  const { mutate: doUpdateStatus } = useUpdateCmsStatus(
    (response) => {
      toast.success(response?.message || t(messages.toast.cms.statusUpdated));
      refetch();
    },
    (error) => {
      toast.error(error?.response?.data?.message || t(messages.toast.cms.statusUpdateFailed));
      refetch();
    }
  );

  const { mutate: doDeleteCms } = useDeleteCms((response) => {
    toast.success(response?.message || t(messages.toast.cms.deleted));
    refetch();
  });

  /** Ask for confirmation, then flip the page's active/inactive status. */
  const handleStatusClick = useCallback(
    (uuid, isActive) => {
      confirmAlert({
        customUI: ({ onClose }) => (
          <div className="alert-box">
            <div className="alert-popup">
              <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
              <h2 className="alert-title">{t(cms.list.status_popup_heading_text)}</h2>
              <div className="d-flex gap-2 justify-content-center">
                <CButton
                  color="primary"
                  className="table-delete-button"
                  onClick={() => {
                    doUpdateStatus({ uuid, isActive: !isActive });
                    onClose();
                  }}
                >
                  {t(cms.list.status_popup_yes_btn_text)}
                </CButton>
                <CButton className="table-primary-button" color="danger" onClick={onClose}>
                  {t(cms.list.status_popup_no_btn_text)}
                </CButton>
              </div>
            </div>
          </div>
        ),
      });
    },
    [t, doUpdateStatus]
  );

  /** Ask for confirmation, then delete the page. */
  const handleDeleteClick = useCallback(
    (uuid) => {
      confirmAlert({
        customUI: ({ onClose }) => (
          <div className="alert-box">
            <div className="alert-popup">
              <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
              <h2 className="alert-title">{t(cms.list.delete_popup_heading_text)}</h2>
              <div className="d-flex gap-2 justify-content-center">
                <CButton
                  color="primary"
                  className="table-delete-button"
                  onClick={() => {
                    doDeleteCms(uuid);
                    onClose();
                  }}
                >
                  {t(cms.list.delete_popup_yes_btn_text)}
                </CButton>
                <CButton className="table-primary-button" color="danger" onClick={onClose}>
                  {t(cms.list.delete_popup_no_btn_text)}
                </CButton>
              </div>
            </div>
          </div>
        ),
      });
    },
    [t, doDeleteCms]
  );

  const columns = useMemo(
    () => [
      {
        id: "created_at",
        accessorKey: "created_at",
        header: t(cms.list.table.created_at_table_col_heading),
        cell: ({ getValue }) => <span>{setFormatDate(getValue()) || "—"}</span>,
      },
      {
        id: "title",
        accessorKey: "title",
        header: t(cms.list.table.title_table_col_heading),
        cell: ({ row }) => (
          <span>
            {row.original.title || "—"}
            {!row.original.has_content && (
              <span className="ms-2 badge bg-warning text-dark">
                {t(cms.list.no_content_badge)}
              </span>
            )}
          </span>
        ),
      },
      {
        id: "version",
        accessorKey: "version",
        header: t(cms.list.table.version_table_col_heading),
        cell: ({ getValue }) => <span>{getValue() || "—"}</span>,
      },
      {
        id: "slug",
        accessorKey: "slug",
        header: t(cms.list.table.slug_table_col_heading),
        cell: ({ getValue }) => <span>{getValue()}</span>,
      },
      {
        id: "updated_at",
        accessorKey: "updated_at",
        header: t(cms.list.table.updated_at_table_col_heading),
        cell: ({ getValue }) => <span>{dateTimeFormat(getValue()) || "—"}</span>,
      },
      {
        id: "is_active",
        accessorKey: "is_active",
        header: t(cms.list.table.status_table_col_heading),
        // Same look as User Master: coloured text, click to change (after confirmation)
        cell: ({ row }) => {
          const isActive = isActiveValue(row.original.is_active);
          return (
            <span
              className={`${isActive ? "text-success" : "text-danger"} cursor-pointer`}
              title={t(cms.list.status_toggle_tooltip)}
              onClick={() => handleStatusClick(row.original.uuid, isActive)}
            >
              {t(isActive ? cms.form.status_active : cms.form.status_inactive)}
            </span>
          );
        },
      },
      {
        id: "action",
        accessorKey: "uuid",
        header: t(cms.list.table.action_table_col_heading),
        cell: ({ row }) => (
          <div className="d-flex gap-2 justify-content-center">
            <div
              className="table-action-text"
              title={t(cms.list.edit_tooltip)}
              onClick={() => handleEditClick(row.original.uuid)}
            >
              <FontAwesomeIcon icon={faEdit} className="list-icon" />
            </div>
            <div
              className="table-action-text"
              title={t(cms.list.delete_tooltip)}
              onClick={() => handleDeleteClick(row.original.uuid)}
            >
              <FontAwesomeIcon icon={faTrash} className="list-icon" />
            </div>
          </div>
        ),
      },
    ],
    [t, handleEditClick, handleDeleteClick, handleStatusClick]
  );

  return (
    <>
      <CRow className="list-align">
        <CCol lg={12} className="d-flex gap-2 justify-content-end flex-wrap">
          <CFormInput
            placeholder={t(cms.list.search_placeholder)}
            type={constants.inputTypes.text}
            value={tempSearchText}
            onChange={handleSearch}
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "300px" }}
          />
          <CButton
            color="primary"
            type="button"
            className="filter-field-space"
            onClick={handleClearFilter}
            style={{ display: tempSearchText.trim() ? "inline-block" : "none" }}
          >
            {t(cms.list.clear_filter_btn_text)}
          </CButton>
          <CButton
            color="primary"
            type="button"
            className="filter-field-space"
            onClick={() => navigate(masters.cms.add)}
          >
            {t(cms.list.add_btn_text)}
          </CButton>
        </CCol>
      </CRow>
      <TNTable
        columns={columns}
        data={cmsList}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => setCurrentPage(pageIndex + 1)}
        t={t}
        pageIndexGet={currentPage - 1}
        isLoading={isLoading}
        className="text-start table-sortable mt-3"
      />
    </>
  );
};

ListCmsPage.breadCrumbArray = [
  { name: breadcrumb_key.home, pathname: dashboard, active: false },
  {
    name: breadcrumb_key.masters?.cms?.list,
    pathname: masters.cms.list,
    active: true,
  },
];

export default ListCmsPage;
